import { useMemo, useState } from "react";
import { Download, Search } from "lucide-react";
import { ENTITIES, type Job } from "@/lib/mockData";
import { useJobsApi } from "@/lib/hooks/useJobsApi";

interface AuditRow {
  id: string;
  ts: string;
  actor: string;
  action: string;
  target: string;
  entity: string;
  patient?: string;
  details: string;
}

/**
 * Static "system" events that don't originate from the job simulator —
 * credential rotations, workflow lifecycle, cross-service webhooks.
 * Ordered newest-first.
 */
const SYSTEM_EVENTS: AuditRow[] = [
  {
    id: "A-1090",
    ts: relativeTs(-35),
    actor: "flow-agent",
    action: "webhook_emitted",
    target: "workflow.started",
    entity: "laoss",
    details: "Published to Command Center",
  },
  {
    id: "A-1089",
    ts: relativeTs(-52),
    actor: "sthomas",
    action: "workflow_promoted_to_active",
    target: "pipa-referral",
    entity: "laoss",
    details: "Two-person approval (Thomas + Perez) — break-in 20/20 complete",
  },
  {
    id: "A-1088",
    ts: relativeTs(-120),
    actor: "nperez",
    action: "credential_rotated",
    target: "anthem-spineone",
    entity: "spineone",
    details: "Anthem portal password reset per payer policy",
  },
  {
    id: "A-1086",
    ts: relativeTs(-240),
    actor: "system",
    action: "scheduled_run.started",
    target: "anthem-elig",
    entity: "nes",
    details: "Morning eligibility batch — 42 patients",
  },
];

export function AuditScreen() {
  const jobsQuery = useJobsApi();
  const jobs = jobsQuery.data?.items ?? [];

  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");

  // Derive job-lifecycle events from the current job snapshot + merge with
  // static system events. When Phase 1d lands, this flips to a GET
  // /api/audit call with the same row shape.
  const rows = useMemo<AuditRow[]>(() => {
    const derived = jobs.flatMap(eventsForJob);
    return [...derived, ...SYSTEM_EVENTS].sort((a, b) => b.ts.localeCompare(a.ts));
  }, [jobs]);

  const filtered = useMemo(() => {
    const qlc = q.toLowerCase();
    return rows.filter((r) => {
      if (entity !== "all" && r.entity !== entity) return false;
      if (action !== "all" && !r.action.includes(action)) return false;
      if (!qlc) return true;
      const hay =
        r.target.toLowerCase() +
        r.actor.toLowerCase() +
        r.details.toLowerCase() +
        (r.patient ?? "").toLowerCase();
      return hay.includes(qlc);
    });
  }, [rows, q, entity, action]);

  const exportTrail = () => {
    const blob = new Blob(
      [
        [
          "id,ts,actor,action,target,entity,patient,details",
          ...filtered.map((r) =>
            [r.id, r.ts, r.actor, r.action, r.target, r.entity, r.patient ?? "", r.details]
              .map(csvCell)
              .join(","),
          ),
        ].join("\n"),
      ],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `flow-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex items-center justify-between border-b border-paper-line bg-paper-0 px-6 py-4">
        <div>
          <h2 className="text-md font-medium text-ink-1">Audit Layer 1</h2>
          <div className="mt-0.5 text-xs text-ink-3">
            Compliance query · 10-year immutable retention · {rows.length} events in this window
          </div>
        </div>
        <button
          type="button"
          onClick={exportTrail}
          className="flex items-center gap-1 rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-1.5 text-sm text-ink-1 hover:bg-paper-tint"
        >
          <Download size={14} /> Export filtered view (CSV)
        </button>
      </header>

      <div className="flex flex-wrap items-end gap-4 border-b border-paper-line bg-paper-1 px-6 py-4">
        <LabeledInput label="Date range">
          <span className="text-xs text-ink-1">Today</span>
        </LabeledInput>
        <LabeledSelect label="Entity" value={entity} onChange={setEntity}>
          <option value="all">All entities</option>
          {ENTITIES.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </LabeledSelect>
        <LabeledSelect label="Action type" value={action} onChange={setAction}>
          <option value="all">All actions</option>
          <option value="job">Job events</option>
          <option value="approval">Approvals</option>
          <option value="credential">Credentials</option>
          <option value="workflow">Workflow lifecycle</option>
          <option value="takeover">Takeovers</option>
          <option value="webhook">Webhooks</option>
        </LabeledSelect>
        <LabeledInput label="Search (patient, target, actor)">
          <div className="flex items-center gap-2 rounded-sm border border-paper-line-strong bg-paper-3 px-2 py-1">
            <Search size={12} className="text-ink-4" aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="try 'J-28441' or 'Gonzalez'"
              className="w-[260px] bg-transparent text-xs text-ink-1 placeholder:text-ink-4 focus:outline-none"
            />
          </div>
        </LabeledInput>
        <span className="ml-auto font-mono text-xs text-ink-3">
          {filtered.length} match{filtered.length === 1 ? "" : "es"}
        </span>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className="overflow-hidden rounded-md border border-paper-line bg-paper-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-paper-line text-left text-[11px] uppercase tracking-wider text-ink-3">
                <Th>Timestamp</Th>
                <Th>Event</Th>
                <Th>Actor</Th>
                <Th>Target</Th>
                <Th>Entity</Th>
                <Th>Patient</Th>
                <Th>Details</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-ink-3">
                    No audit entries matching those filters.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-paper-line last:border-0 hover:bg-paper-tint">
                  <Td>
                    <span className="font-mono text-xs text-ink-3">{r.ts}</span>
                  </Td>
                  <Td>
                    <div className="font-mono text-[12px] text-ink-1">{r.action}</div>
                    <div className="font-mono text-[10px] text-ink-4">{r.id}</div>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs text-ink-2">{r.actor}</span>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs text-ink-1">{r.target}</span>
                  </Td>
                  <Td>
                    <span className="uppercase tracking-wider text-ink-3">{r.entity}</span>
                  </Td>
                  <Td>
                    {r.patient ? (
                      <span className="text-ink-1">{r.patient}</span>
                    ) : (
                      <span className="text-ink-4">—</span>
                    )}
                  </Td>
                  <Td>
                    <span className="text-ink-2">{r.details}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Event derivation
// ──────────────────────────────────────────────────────────────

function eventsForJob(j: Job): AuditRow[] {
  const base = {
    target: j.id,
    entity: j.entity.id,
    patient: j.patient?.name,
  };
  const start = startTs(j);
  const events: AuditRow[] = [
    {
      id: `A-${j.id}-start`,
      ts: start,
      actor: "flow-agent",
      action: "job_started",
      details: j.workflow.name,
      ...base,
    },
  ];

  if (j.status === "approval") {
    events.push({
      id: `A-${j.id}-approvalreq`,
      ts: stepTs(start, 1),
      actor: "flow-agent",
      action: "approval_requested",
      details: `Phase ${j.curPhase + 1} is a checkpoint — awaiting supervisor`,
      ...base,
    });
  }

  if (j.status === "success") {
    events.push({
      id: `A-${j.id}-success`,
      ts: stepTs(start, 2),
      actor: "flow-agent",
      action: "job_succeeded",
      details: j.resultSummary,
      ...base,
    });
  }

  if (j.status === "human") {
    events.push({
      id: `A-${j.id}-takeoverinit`,
      ts: stepTs(start, 1),
      actor: "nperez",
      action: "takeover_initiated",
      details: `Supervisor stepped in at phase ${j.curPhase + 1}`,
      ...base,
    });
    events.push({
      id: `A-${j.id}-takeovercompleted`,
      ts: stepTs(start, 2),
      actor: j.user.id,
      action: "takeover_completed",
      details: j.resultSummary,
      ...base,
    });
  }

  if (j.status === "failed") {
    events.push({
      id: `A-${j.id}-fail`,
      ts: stepTs(start, 2),
      actor: "flow-agent",
      action: "job_failed",
      details: j.resultSummary,
      ...base,
    });
  }

  if (j.status === "takeover") {
    events.push({
      id: `A-${j.id}-takeoverinit`,
      ts: stepTs(start, 1),
      actor: "flow-agent",
      action: "takeover_initiated",
      details: "Escalated to supervisor — see job detail",
      ...base,
    });
  }

  return events;
}

/**
 * Build a reasonable ISO timestamp for a job's start. The simulator only
 * stores "HH:MM"; backfill today's date so sort + display both work.
 */
function startTs(j: Job): string {
  const today = new Date().toISOString().slice(0, 10);
  const hm = /^\d{1,2}:\d{2}$/.test(j.started) ? j.started.padStart(5, "0") : "00:00";
  return `${today} ${hm}:00Z`;
}

function stepTs(base: string, steps: number): string {
  // Push ts forward by `steps` minutes. Good enough for sort stability.
  const m = base.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2}):(\d{2})Z$/);
  if (!m) return base;
  const date = m[1]!;
  const hh = parseInt(m[2]!, 10);
  const mm = parseInt(m[3]!, 10);
  const ss = m[4]!;
  const total = hh * 60 + mm + steps;
  const nh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const nm = String(total % 60).padStart(2, "0");
  return `${date} ${nh}:${nm}:${ss}Z`;
}

function relativeTs(minutesAgo: number): string {
  const d = new Date(Date.now() + minutesAgo * 60_000);
  const date = d.toISOString().slice(0, 10);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${date} ${hh}:${mm}:${ss}Z`;
}

function csvCell(v: string | undefined): string {
  const s = v ?? "";
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// ──────────────────────────────────────────────────────────────

function LabeledInput({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-ink-4">{label}</span>
      {children}
    </label>
  );
}

function LabeledSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <LabeledInput label={label}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm border border-paper-line-strong bg-paper-3 px-2 py-1 text-xs text-ink-1 focus:outline-none focus:ring-2 focus:ring-teal-500"
      >
        {children}
      </select>
    </LabeledInput>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2 align-middle">{children}</td>;
}
